var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import * as R from 'ramda';
import * as F from '../../models/formula.types';
import { XbpmBlockType } from '../../models/xbpm.enums';
import { ElementDefType } from '../../models/def.types';
import { execFormula } from './formula.utils';
import { isElementField, isElementBlock, mapElementTreeAsImmutable, mapFormDefFlatten } from './_.utils';
import { applyPropertyChangeAsMutable } from './def.utils';
var FORMULA_ELEMENTS_TRANSCLUDE = [XbpmBlockType.PANEL];
// transform to simple form definition
var isGetterDefined = function (element, name) {
    if (element) {
        var descriptor = Object.getOwnPropertyDescriptor(element, name);
        return !!descriptor && !!descriptor.get;
    }
    return false;
};
export var mapFormulaElementToFormElement = mapElementTreeAsImmutable(function (it) {
    return R.omit(__spreadArrays([
        'parent',
        'block',
        'rootBlock',
        'fields',
        'blocks',
        'isField',
        'isBlock',
        'isValueChanged',
        'isValueFetching',
        'isValid',
        'isTouched'
    ], (isGetterDefined(it, 'value') ? ['value'] : [])), it);
});
// calc element properties
var complementFormula = function (formula) {
    return !formula
        ? null
        : R.pipe(formula, function (res) { return (res instanceof Promise ? res.then(R.not) : R.not(res)); });
};
var calcElementProperties = function (ctx) { return function (element) {
    var formulas = element.frontFormulas;
    var formulasPairs = [
        [F.FormulaChangeProperty.VALUE, isElementField(element) ? formulas.formulaFrontCalc : null],
        [F.FormulaChangeProperty.OPTIONS, formulas.formulaFrontOptions],
        [F.FormulaChangeProperty.READONLY, formulas.formulaFrontReadOnly],
        [F.FormulaChangeProperty.DISABLED, complementFormula(formulas.formulaFrontEnabled)],
        [F.FormulaChangeProperty.HIDDEN, complementFormula(formulas.formulaFrontVisibility)],
    ];
    var changed = false;
    formulasPairs.filter(R.path([1])).forEach(function (_a) {
        var key = _a[0], fn = _a[1];
        var value = execFormula(fn, element);
        if (value instanceof Promise) {
            ctx.handleAsyncPropertyChange(element.id, key, value);
        }
        else if (!R.equals(element[key], value)) {
            ctx.markPropertyChangeAsSync(element.id, key);
            changed = applyPropertyChangeAsMutable(element, key, value) || changed;
        }
    });
    return changed;
}; };
// create formula element function
export var createFormulaElementDefFn = function (env) {
    // transclude elements
    var isForTransclude = R.pipe(R.prop('kind'), R.contains(R.__, FORMULA_ELEMENTS_TRANSCLUDE));
    var transcludeElements = function (items) {
        return items.map(R.when(isForTransclude, function (it) { return (it.elements ? transcludeElements(it.elements) : []); }));
    };
    var flattenTranscludeElements = R.pipe(transcludeElements, R.flatten);
    // formula element getter (fields/blocks)
    var createElementsGetter = function (filterFn) {
        return function (code) {
            var block = isElementField(this) ? this.block : this;
            var elements = block && block.$type === ElementDefType.BLOCK
                ? flattenTranscludeElements(block.elements || []).filter(filterFn)
                : [];
            return code ? elements.find(R.propEq('code', code)) : elements;
        };
    };
    // find root block
    var getRootBlock = function (el) { return (el && el.block ? getRootBlock(el.block) : el); };
    // get form value
    var getFormValue = function (el, isRoot) {
        var _a, _b;
        if (isRoot === void 0) { isRoot = false; }
        if (!el) {
            return null;
        }
        var code = el.code, isTranscludeForCalc = el.frontOptions.isTranscludeForCalc, formulaFrontCalc = el.frontFormulas.formulaFrontCalc;
        // block values
        if (isElementBlock(el)) {
            // block content values
            var values = formulaFrontCalc ? execFormula(formulaFrontCalc, el) : getBlockValue(el);
            // block own values (ex: table row)
            var ownValues = !!formulaFrontCalc || isGetterDefined(el, 'value') ? null : el.value;
            if (ownValues) {
                if (Array.isArray(ownValues)) {
                    if (el.kind === XbpmBlockType.TABLE_ROW) {
                        return __assign(__assign({}, R.fromPairs(ownValues.map(function (it) { return [it.code, it.value]; }))), (Array.isArray(values) ? { values: values } : values));
                    }
                    values = __assign({ value: ownValues }, (Array.isArray(values) ? { values: values } : values));
                }
                else {
                    values = __assign(__assign({}, ownValues), (Array.isArray(values) ? { values: values } : values));
                }
            }
            return isTranscludeForCalc && (isRoot || !Array.isArray(values)) ? values : (_a = {}, _a[code] = values, _a);
        }
        // field value
        return _b = {}, _b[code] = el.value, _b;
    };
    // get block values
    var getBlockValue = function (block) {
        var elements = isElementBlock(block)
            ? __spreadArrays(block.fields(), block.blocks()) : [];
        return block.kind === XbpmBlockType.TABLE ? getBlockArrValue(elements) : getBlockObjValue(elements);
    };
    var getBlockObjValue = R.pipe(R.map(function (it) { return (it && !it.frontOptions.isExcludeForCalc ? getFormValue(it) : null); }), R.filter(function (it) { return !!it; }), R.mergeAll);
    var getBlockArrValue = R.map(function (it) {
        var value = getFormValue(it);
        return value ? value[it.code] : null;
    });
    var defineBlockValue = function (block) {
        return Object.defineProperty(block, 'value', {
            get: function () {
                return getFormValue(this, true);
            },
        });
    };
    // map formula element block
    var mapFormulaElementDefIfBlock = function (element) {
        if (isElementBlock(element)) {
            // map block elements
            var elements = element.elements || [];
            element.elements = elements.map(function (it) { return mapFormulaElementDef(it, element); });
            // define block calc value getter
            if (!R.has('value', element)) {
                defineBlockValue(element);
            }
        }
        return element;
    };
    // map formula element recursive
    var mapFormulaElementDef = function (element, parent) {
        return !element
            ? null
            : mapFormulaElementDefIfBlock(__assign(__assign({}, element), { get parent() {
                    return (parent && isForTransclude(parent) ? parent.parent : parent) || null;
                },
                get block() {
                    return this.parent;
                },
                get rootBlock() {
                    return getRootBlock(this);
                }, fields: createElementsGetter(isElementField), blocks: createElementsGetter(isElementBlock), isValueChanged: function () {
                    return env.isElementValueChanged(this);
                },
                isValueFetching: function () {
                    return env.isElementValueFetching(this);
                },
                isValid: function () {
                    return env.isElementValid(this);
                },
                isTouched: function () {
                    return env.isElementTouched(this);
                },
                isField: function () {
                    return isElementField(this);
                },
                isBlock: function () {
                    return isElementBlock(this);
                } }));
    };
    return function (element) { return mapFormulaElementDef(element); };
};
// create form calculator frame
export var createFormCalculatorFrame = function (ctx, form) {
    var calcElement = calcElementProperties(ctx);
    var frame = mapFormDefFlatten(function (it) {
        if (!it.frontFormulas || R.isEmpty(it.frontFormulas)) {
            return undefined;
        }
        return function () { return calcElement(it); };
    })(form);
    return new Map(Object.entries(frame));
};
//# sourceMappingURL=calc.utils.js.map